(ns manage.adset-preview-panel.db.variants
  (:require [clojure.string :as str]
            [common.db.interceptors :refer [forwarded-event]]
            [manage.adset-preview-panel.util.context
             :refer [cfn->context context->fields context->unparse-values fields->context overwrite-context-values update-fields-value]]
            [re-frame.core :as rf]))

(def initial-state {;; context for if the page was loaded with query-params for context
                    :shared-context nil

                    ;; selected context is used to evaluate the cfn
                    :selected-variant-context nil

                    ;; context fields used within the view
                    :context-fields []

                    :use-request-context? false})

(rf/reg-event-fx :adset-preview.variants/init
  (fn [{:keys [db]} _]
    {:db (assoc db :adset-preview.variants initial-state)
     :forward-events [{:register :adset-preview.variants/get-your-context-success
                       :events #{:resource.adset-preview/get-your-context-success}
                       :dispatch-to [:adset-preview.variants/get-your-context-success]}
                      {:register :adset-preview.variants/get-your-context-failure
                       :events #{:resource.adset-preview/get-your-context-failure}
                       :dispatch-to [:adset-preview.notifications/show-short
                                     :your-context-error
                                     {:type "error"
                                      :message "Something went wrong fetching your context."}]}]}))

;; Get your context
(rf/reg-event-fx :adset-preview.variants/get-your-context
  [(rf/inject-cofx :sub [:resource.content-function-version/active-version {:only-staged true}])]
  (fn [{:resource.content-function-version/keys [active-version]} _]
    {:dispatch [:resource.adset-preview/get-your-context (:name active-version)]}))

(rf/reg-event-fx :adset-preview.variants/set-adset-preview
  [forwarded-event
   (rf/inject-cofx :namespace-values :adset-preview.core)]
  (fn [{:keys [db] :adset-preview.core/keys [shared-context]} _]
    (cond-> {:fx [[:dispatch [:adset-preview.variants/cfn->selected-context]]]}
      shared-context (assoc :db (assoc-in db [:adset-preview.variants :shared-context] shared-context)))))

(rf/reg-event-fx :adset-preview.variants/destroy
  (fn [_ _]
    {:forward-events [{:unregister :adset-preview.variants/get-your-context-success}
                      {:unregister :adset-preview.variants/get-your-context-failure}]}))

;; populate content function and create a context of it
(rf/reg-event-fx :adset-preview.variants/cfn->selected-context
  [(rf/inject-cofx :namespace-values :adset-preview.core)
   (rf/inject-cofx :namespace-values :adset-preview.variants)]
  (fn [{:keys [db]
        :adset-preview.variants/keys [selected-variant-context]
        :adset-preview.core/keys [content-function schema-urns context-schemas]} _]
    (let [shared-context (get-in db [:adset-preview.variants :shared-context])
          shared-context* (context->unparse-values schema-urns context-schemas shared-context)
          context (as-> (cfn->context content-function) $
                        (dissoc $ :creative)
                        (overwrite-context-values (or shared-context* selected-variant-context) $))
          context-fields (context->fields schema-urns context-schemas context)]
      {:db (update-in db [:adset-preview.variants] assoc
                      :context-fields [context-fields])
       :dispatch [:adset-preview.variants/set-selected-context (or shared-context context)]})))

;; Set variant context which is used to evaluate cfn for update the templates
(rf/reg-event-fx :adset-preview.variants/set-selected-context
  (fn [{:keys [db]} [_ context]]
    {:db (assoc-in db [:adset-preview.variants :selected-variant-context] context)}))

;; Update context fields value of given idx where the context field
;; match given predicate, for example:
;; (rf/dispatch [:adset-preview.variants/update-fields-value) 0 (constantly true) "foo"])
;; will update the context-fields index 0 with all entries values to "foo"
(rf/reg-event-fx :adset-preview.variants/update-fields-value
  [(rf/inject-cofx :namespace-values :adset-preview.variants)
   (rf/inject-cofx :namespace-values :adset-preview.core)]
  (fn [{:keys [db]
        :adset-preview.core/keys [channel]
        :adset-preview.variants/keys [context-fields]}
       [_ pred value]]
    (let [variants-fields (update-in context-fields [0]
                                     #(update-fields-value % pred value))
          fields (first variants-fields)
          variant-context (fields->context fields)]
      {:db (cond-> db
             (= "display" channel) (assoc-in [:adset-preview.variants :context-fields] variants-fields))
       :fx [[:dispatch [:adset-preview.variants/set-selected-context variant-context]]]})))

;; Update context field value of given idx with given value
;; where context field matches entry :name
;; Example:
;; (rf/dispatch [:adset-preview.variants/context-value-change)
;;   {:context-name "Device - Operating system"} "new value"])
;; will update context-fields index 0 with all entries values matching passed :name
;; "Device - Operating system" to "new value"
(rf/reg-event-fx :adset-preview.variants/context-value-change
  (fn [{:keys [db]} [_ {:keys [context-name]} value]]
    {:db (assoc-in db [:adset-preview.variants :use-request-context?] false)
     :dispatch [:adset-preview.variants/update-fields-value
                (comp #(= context-name %) :name)
                value]}))

;; Set all context field values to nil by given variant idx
(rf/reg-event-fx :adset-preview.variants/clear-variant-context
  (fn [{:keys [db]} _]
    {:db (assoc-in db [:adset-preview.variants :use-request-context?] false)
     :dispatch [:adset-preview.variants/update-fields-value
                (constantly true)
                nil]}))

;; Populate the :poi context from a selected POI location.
;; Formats the location's data (dropping :timezone, adding :external-id;
;; vector values are rendered as a readable string) and merges it into the
;; context, first clearing any existing :poi values so stale keys don't linger.
;; Example:
;; (rf/dispatch [:adset-preview.variants/select-poi-location
;;   {:external-id "store-42" :data {:name "Amsterdam" :timezone "Europe/Amsterdam"}}])
;; sets :poi to {:external-id "store-42" :name "Amsterdam"} (timezone dropped).
(rf/reg-event-fx :adset-preview.variants/select-poi-location
  [(rf/inject-cofx :namespace-values :adset-preview.variants)
   (rf/inject-cofx :namespace-values :adset-preview.core)]
  (fn [{:keys [db]
        :adset-preview.core/keys [schema-urns context-schemas]
        :adset-preview.variants/keys [selected-variant-context]}
       [_ location]]
    (let [loc-data (merge (dissoc (:data location) :timezone)
                          (select-keys location [:external-id]))
          format-val (fn [v]
                       (cond
                         (and (vector? v) (every? vector? v))
                         (->> v (map #(str/join "-" %)) (str/join " "))
                         (vector? v) (str/join ", " v)
                         :else (str v)))
          poi-values (reduce-kv (fn [m k v]
                                  (if (some? v) (assoc m k (format-val v)) m))
                                {}
                                loc-data)
          existing-poi (get selected-variant-context :poi {})
          cleared-poi (zipmap (keys existing-poi) (repeat nil))
          merged (assoc selected-variant-context :poi (merge cleared-poi poi-values))
          context-fields (context->fields schema-urns context-schemas merged)]
      {:db (-> db
               (assoc-in [:adset-preview.variants :context-fields] [context-fields])
               (assoc-in [:adset-preview.variants :use-request-context?] false))
       :dispatch [:adset-preview.variants/set-selected-context
                  (fields->context context-fields)]})))

;; "Your context" fetched successfully
(rf/reg-event-fx :adset-preview.variants/get-your-context-success
  [forwarded-event
   (rf/inject-cofx :namespace-values :adset-preview.variants)
   (rf/inject-cofx :namespace-values :adset-preview.core)]
  (fn [{:keys [db]
        :adset-preview.core/keys [schema-urns context-schemas]
        :adset-preview.variants/keys [selected-variant-context]}
       [_ context]]
    (let [unparse-ctx (partial context->unparse-values schema-urns context-schemas)
          context-fields (context->fields
                          schema-urns
                          context-schemas
                          (overwrite-context-values (unparse-ctx context)
                                                    (unparse-ctx selected-variant-context)))]
      {:db (-> db
               (assoc-in [:adset-preview.variants :context-fields] [context-fields])
               (assoc-in [:adset-preview.variants :use-request-context?] true))
       :dispatch [:adset-preview.variants/set-selected-context
                  (fields->context context-fields)]})))

;; Subscriptions
(rf/reg-sub :adset-preview.variants/value
  (fn [db [_ path default]]
    (get-in db [:adset-preview.variants path] default)))

(rf/reg-sub :adset-preview.variants/selected-variant-fields
  :<- [:adset-preview.variants/value :context-fields]
  (fn [context-fields _]
    (first context-fields)))

(rf/reg-sub :adset-preview.variants/use-request-context?
  (fn [db _]
    (get-in db [:adset-preview.variants :use-request-context?] false)))
